import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon, Photo, PrincipalLink } from '../../';
import { useGlobalState } from '../../../hooks';
import { Asset, AssetName, formatData, Operation } from '../../../helpers';
import { formatChangelogValue } from '../helpers';

export const ChangelogItem = ({ data, objectName, className }) => {
  const [hidden, setHidden] = useState(true);
  const [displayNames] = useGlobalState('displayNames');

  const toggleItem = state => {
    setHidden(state || !hidden);
  };

  let name = '';

  if (data.is_service_account_user) {
    name = 'Datasource Sync';
  } else if (data.is_existing_user) {
    name = data.changed_by_user_fullname;
  } else {
    name = data.changed_by_user_username;
  }

  let header;
  let headerProperties = '';
  let headerFields = '';

  const changedPropertyCount = data.changedData ? data.changedData.length : 0;
  const changedFieldCount = data.fields ? data.fields.length : 0;
  const changedUserCount = data.users ? data.users.length : 0;
  const changedTotalCount = changedFieldCount + changedPropertyCount;

  switch (data.operation) {
    case 'I':
      header = 'Added to Katalogue';
      break;
    case 'D':
      header = 'Deleted from Katalogue';
      break;
    case 'C':
      // A conversion moves the asset between the Entity and Attribute tables, so it names both kinds.
      // Which way it went comes from the entry itself and must not be derived from objectName: that is
      // the kind the asset is now, and converting rewrites the whole history onto the current kind, so
      // an asset converted more than once would report every conversion in the same direction. The
      // fallback covers an entry from an API that does not report the direction.
      header = `Converted to ${AssetName[data.converted_to_object_name || objectName]} from ${
        AssetName[
          data.converted_from_object_name ||
            (objectName === Asset.Entity ? Asset.Attribute : Asset.Entity)
        ]
      }`;
      headerProperties =
        changedPropertyCount > 0
          ? `${changedPropertyCount} propert${changedPropertyCount > 1 ? 'ies' : 'y'} changed`
          : '';
      break;
    case 'U':
      headerProperties = `${changedPropertyCount} propert${changedPropertyCount > 1 ? 'ies' : 'y'} changed`;
      switch (objectName) {
        case Asset.Dataset:
          headerFields = `${changedFieldCount} field${changedFieldCount > 1 ? 's' : ''} changed`;
          break;
        case Asset.UserGroup:
          headerFields = `${changedUserCount} member${changedUserCount > 1 ? 's' : ''} changed`;
          break;
      }
      break;
    default:
      break;
  }

  // A conversion expands like an update so the properties changed in the same save can be read, but
  // only when there are any — an expander over an empty table would be a dead control.
  const expandable = data.operation === 'U' || (data.operation === 'C' && changedPropertyCount > 0);

  return (
    <div
      className={'ChangelogItem' + (hidden ? ' hidden' : '') + (className ? ' ' + className : '')}
    >
      <div className="changelog-item-header">
        {data.is_service_account_user && data.job_id && data.task_id && objectName !== 'user' ? (
          <Link to={`/tasks/${data.task_id}/${data.job_id}?tab=changelog&object=${objectName}`}>
            <PrincipalLink
              principalName={name}
              principalPhoto={data.changed_by_user_photo}
              userTitle={undefined}
              userDepartment={undefined}
              fetchPrincipalData={false}
              header={formatData(data.created_timestamp, 'datetime')}
              photoPosition="right"
              className="width-160 photo-background link"
              type={data.is_service_account_user ? 'SERVICE_ACCOUNT' : 'USER'}
            />
          </Link>
        ) : (
          <PrincipalLink
            userId={data.changed_by_user_id}
            principalName={name}
            principalPhoto={data.changed_by_user_photo}
            principalType="user"
            userTitle={undefined}
            userDepartment={undefined}
            fetchPrincipalData={false}
            header={formatData(data.created_timestamp, 'datetime')}
            photoPosition="right"
            className="width-160 photo-background"
            type={data.is_service_account_user ? 'SERVICE_ACCOUNT' : 'USER'}
          />
        )}
      </div>
      <div
        className={'changelog-item-body' + (expandable ? ' expandable' : '')}
        onClick={() => hidden && expandable && toggleItem()}
      >
        {expandable ? (
          <React.Fragment>
            {(changedFieldCount > 0 || changedUserCount > 0) && (
              <div onClick={() => hidden && toggleItem(false)}>
                <div
                  className={'changelog-item-body-header'}
                  onClick={() => !hidden && toggleItem(false)}
                >
                  {headerFields}
                  {!hidden && <div className="toggle-item" onClick={() => toggleItem(false)}></div>}
                </div>
                <div className="changelog-item-body-text">
                  {data.fields && (
                    <table className="field-list">
                      <thead>
                        <tr>
                          <th>Operation</th>
                          <th></th>
                          <th>Field</th>
                          <th>Datatype</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.fields.map((field, index) => {
                          return (
                            <tr key={index}>
                              <td className="type-pill">
                                <span className={Operation[field.operation].className}>
                                  {Operation[field.operation].label}
                                </span>
                              </td>
                              <td className="type-icon">
                                <Icon
                                  name={field.datatype_category}
                                  tooltip={field.datatype_fullname}
                                />
                              </td>
                              <td className="type-fieldname">
                                {!hidden && field.operation !== 'D' ? (
                                  <Link
                                    className="link"
                                    to={`/systems/${field.system_id}/${field.datasource_id}/${field.dataset_group_id}/${field.dataset_id}/${field.field_id}`}
                                  >
                                    {field.field_name}
                                  </Link>
                                ) : (
                                  <React.Fragment>{field.field_name}</React.Fragment>
                                )}
                              </td>
                              <td className="small-text">{field.datatype_fullname}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  {data.users && (
                    <table className="field-list">
                      <thead>
                        <tr>
                          <th>Operation</th>
                          <th></th>
                          <th>User</th>
                          <th>Username</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.users.map((user, index) => {
                          return (
                            <tr key={index}>
                              <td className="type-pill">
                                <span className={Operation[user.operation].className}>
                                  {Operation[user.operation].label}
                                </span>
                              </td>
                              <td className="type-owner">
                                <Photo data={user.user_photo} name={user.user_fullname} />
                              </td>
                              <td className="type-fieldname">
                                {!hidden && user.operation !== 'D' && user.user_fullname ? (
                                  <Link className="link" to={`/users/${user.user_id}`}>
                                    {user.user_fullname}
                                  </Link>
                                ) : (
                                  <React.Fragment>
                                    {user.user_fullname || 'Deleted user'}
                                  </React.Fragment>
                                )}
                              </td>
                              <td className="small-text">{user.user_username}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
            {changedPropertyCount > 0 && (
              <div onClick={() => hidden && toggleItem(false)}>
                <div className="changelog-item-body-header strong">{header}</div>
                <div
                  className={
                    'changelog-item-body-header' +
                    (changedPropertyCount !== changedTotalCount ? ' second-row' : '')
                  }
                  onClick={() =>
                    !hidden && changedPropertyCount === changedTotalCount && toggleItem(false)
                  }
                >
                  {headerProperties}
                  {!hidden && <div className="toggle-item" onClick={() => toggleItem(false)}></div>}
                </div>
                <div className="changelog-item-body-text">
                  <table>
                    <thead>
                      <tr>
                        <th>Property</th>
                        <th>To</th>
                        <th>From</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.changedData &&
                        data.changedData.map((value, index) => {
                          return (
                            <tr key={index}>
                              <td>
                                {displayNames[value.property]?.display_name || value.property}
                              </td>
                              <td>{formatChangelogValue('new', value)}</td>
                              <td>{formatChangelogValue('old', value)}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </React.Fragment>
        ) : (
          <div className="changelog-item-body-header strong">{header}</div>
        )}
      </div>
    </div>
  );
};
